
<script setup lang="ts">
import { vOnClickOutside } from '@vueuse/components'
import { useForm, useField } from 'vee-validate'
import ChevronRightIcon from '@heroicons/vue/20/solid/ChevronRightIcon'
import Button from '~/components/UI/Button.vue'
import InputText from '~/components/UI/InputText.vue'
import Privacy from '~/components/Privacy.vue'

const { handleSubmit, meta } = useForm()
const { isNewsletterDialogOpen, loading } = storeToRefs(useUIStore())
const { closeNewsletterDialog } = useUIStore()
const { email: storeEmail } = storeToRefs(useUserStore())
const { setEmail } = useUserStore()
const { sendNewsletterSubscriptionEmail } = useEmail()

const isSubmitted = ref(false)

const isOpen = computed(() => !!isNewsletterDialogOpen.value)

const isFormValid = computed(() => meta.value.valid);

const { value: name, meta: nameMeta, errorMessage: nameError } = useField<string>('name', { requiredRule })
const { value: surname, meta: surnameMeta, errorMessage: surnameError } = useField<string>('surname', { requiredRule })
const { value: email, meta: emailMeta, errorMessage: emailError } = useField<string>('email', { requiredRule, emailRule })
const { value: privacy1, meta: privacy1Meta, errorMessage: privacy1Error } = useField<boolean>('privacy1', { requiredRule })
const { value: privacy1b, meta: privacy1bMeta, errorMessage: privacy1bError } = useField<boolean>('privacy1b', { requiredRule })
const { value: privacy2 } = useField<boolean>('privacy2')
const { value: privacy3 } = useField<boolean>('privacy3')
const { value: privacy4 } = useField<boolean>('privacy4')


const togglePrivacy1 = (value: boolean) => {
  privacy1.value = value
}

const togglePrivacy1b = (value: boolean) => {
  privacy1b.value = value
}

const togglePrivacy2 = (value: boolean) => {
  privacy2.value = value
}

const togglePrivacy3 = (value: boolean) => {
  privacy3.value = value
}

const togglePrivacy4 = (value: boolean) => {
  privacy4.value = value
}

const showNameError = computed(() =>
  (isSubmitted.value || nameMeta.touched) && !nameMeta.valid
)

const showSurameError = computed(() =>
  (isSubmitted.value || surnameMeta.touched) && !surnameMeta.valid
)

const showEmailError = computed(() =>
  (isSubmitted.value || emailMeta.touched) && !emailMeta.valid
)

const showPrivacy1Error = computed(() =>
  (isSubmitted.value || privacy1Meta.touched) && !privacy1Meta.valid
)

const showPrivacy1bError = computed(() =>
  (isSubmitted.value || privacy1bMeta.touched) && !privacy1bMeta.valid
)

const maybeSubmit = () => {
  isSubmitted.value = true
  submit()
}

const submit = handleSubmit(async () => {
  sendNewsletterSubscriptionEmail(email.value, {
    privacy1: privacy1.value,
    privacy1b: privacy1b.value,
    privacy2: privacy2.value,
    privacy3: privacy3.value,
    privacy4: privacy4.value,
  })
  closeNewsletterDialog()
})

watch(() => storeEmail.value, (value) => {
  if (value === email.value) {
    return
  }
  email.value = value
}, { immediate: true })

const onEmailInput = (value: string) => {
  if (!emailMeta.valid) {
    return
  }

  setEmail(value)
}
</script>

<template>
  <form class="flex justify-content-center" @submit="handleSubmit(maybeSubmit)">
    <Dialog v-on-click-outside="closeNewsletterDialog" :visible="isOpen" @hide="closeNewsletterDialog" modal closable
      close-on-escape :draggable="false" :style="{ width: '50rem' }" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
      @update:visible="closeNewsletterDialog" :pt="{
        'icons': { class: 'self-start' },
      }">
      <template #header>
        <div class="w-full flex flex-col items-center justify-center gap-2">
          <span class="text-2xl sm:text-4xl text-slate-600 text-center font-bold white-space-nowrap">
            Resta aggiornato
          </span>
          <span class="text-lg sm:text-2xl text-primary text-center white-space-nowrap">
            iscriviti alla <span class="font-bold">Newsletter</span>!
          </span>
        </div>
      </template>
      <div class="flex flex-col gap-6 max-h-[60vh] overflow-y-auto	">
        <div class="flex flex-wrap gap-4">
          <InputText id="rate-dialog-name" class="w-full md:w-[calc(50%-0.5rem)]" v-model="name" type="text"
            placeholder="Nome" :has-error="showNameError" :required="true" autofocus>
            <template #label>Nome <span class="text-xs" aria-hidden="true">*</span></template>
            <template #error>{{ nameError }}</template>
          </InputText>
          <InputText id="rate-dialog-surname" class="w-full sm:w-[calc(50%-0.5rem)]" v-model="surname" type="text"
            placeholder="Cognome" :has-error="showSurameError" :required="true">
            <template #label>Cognome <span class="text-xs" aria-hidden="true">*</span></template>
            <template #error>{{ surnameError }}</template>
          </InputText>
          <InputText id="rate-dialog-email" class="w-full sm:w-[calc(50%-0.5rem)]" v-model="email" type="email"
            placeholder="Email" :has-error="showEmailError" :required="true" @input="onEmailInput">
            <template #label>Email <span class="text-xs" aria-hidden="true">*</span></template>
            <template #error>{{ emailError }}</template>
          </InputText>
        </div>
        <div class="w-full flex flex-col gap-4 items-start justify-center">
          <Privacy
            :privacy1="privacy1" :privacy1b="privacy1b"
            :privacy2="privacy2" :privacy3="privacy3" :privacy4="privacy4"
            :has-error="showPrivacy1Error" :error="privacy1Error"
            :has-error1b="showPrivacy1bError" :error1b="privacy1bError"
            @change:privacy1="togglePrivacy1" @change:privacy1b="togglePrivacy1b"
            @change:privacy2="togglePrivacy2" @change:privacy3="togglePrivacy3" @change:privacy4="togglePrivacy4" />
        </div>
      </div>
      <template #footer>
        <div class="w-full flex flex-col gap-8 items-center justify-center">
          <Button v-tooltip.top="{
            value: 'Per continuare completa correttamente tutti i campi contrassegnati da asterisco o evidenziati in rosso.',
            disabled: loading || !isSubmitted || isFormValid
          }" class="w-full sm:w-1/3 flex items-center justify-center gap-1" :disabled="isSubmitted && !isFormValid"
            @click="maybeSubmit">
            Invia
            <ChevronRightIcon class="h-6 w-6" />
          </Button>
        </div>
      </template>
    </Dialog>
  </form>
</template>